import React, { useEffect, useState } from 'react';
import '../Foodstuffs.less';
import * as api from 'util/api.js';
import {
  Table as AntTable,
  Button,
  Typography,
  Avatar,
  Alert,
} from 'antd';
import { CustomTable } from 'components';

const { Column } = AntTable;
const { Text } = Typography;

/** Truthy placeholder so CustomTable does not replace the cell with N/A before render (empty SAP). */
const EMPTY_SAP = '\u200b';

function renderCopyableValue(value) {
  if (value == null || value === EMPTY_SAP || String(value).trim() === '') {
    return <Text type="secondary">—</Text>;
  }
  const s = String(value);
  return <Text copyable>{s}</Text>;
}

const TABLE_ID = 'foodstuffs-both-islands-sku-sap';

const FoodstuffsSkuSapTable = () => {
  const [tableData, setTableData] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getProducts(), api.getAllProductSkus()])
      .then(([products, allSkus]) => {
        if (cancelled) return;
        if (!Array.isArray(products) || !Array.isArray(allSkus)) {
          setTableData([]);
          return;
        }
        const webRenderUrlByProductId = new Map(
          products.map((p) => [String(p.id), p.webRenderUrl]),
        );
        const tapBadgeUrlByProductId = new Map(
          products.map((p) => [String(p.id), p.tapBadgeUrl]),
        );
        const rows = allSkus
          .filter((sku) => {
            const t = sku.containerType ?? sku.container_type;
            return t !== 'keg';
          })
          .map((sku) => {
            const pid = sku.productMatrixId ?? sku.product_matrix_id;
            const idKey = String(pid);
            const containerType = sku.containerType ?? sku.container_type;
            const webRenderUrl = webRenderUrlByProductId.get(idKey);
            const tapBadgeUrl = tapBadgeUrlByProductId.get(idKey);
            const avatarUrl = containerType === 'can' ? webRenderUrl : tapBadgeUrl;
            const fullSkuName = (sku.name && String(sku.name).trim() !== '')
              ? String(sku.name)
              : 'Unnamed SKU';
            const ni = sku.foodstuffsArticleNi != null && String(sku.foodstuffsArticleNi).trim() !== ''
              ? String(sku.foodstuffsArticleNi)
              : EMPTY_SAP;
            const si = sku.foodstuffsArticleSi != null && String(sku.foodstuffsArticleSi).trim() !== ''
              ? String(sku.foodstuffsArticleSi)
              : EMPTY_SAP;
            return {
              key: `${pid}-${sku.key}`,
              productMatrixId: pid,
              avatarUrl,
              sku: fullSkuName,
              ni,
              si,
            };
          });
        setTableData(rows);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Alert
        type="warning"
        showIcon
        message="SAP migration in progress"
        description="Foodstuffs SAP numbers are undergoing migration. This source of data may be inaccurate for a while."
        style={{ marginBottom: 16 }}
      />
      <CustomTable
        id={TABLE_ID}
        pluralLabel="SKUs"
        searchableColumns={['sku', 'ni', 'si']}
        dataSource={tableData}
      >
        <Column
          title={null}
          dataIndex="avatarUrl"
          key="avatar"
          width={64}
          className="avatar-column"
          render={(url) => <Avatar src={url} />}
        />
        <Column
          title="SKU"
          key="sku"
          dataIndex="sku"
          sortType="string"
          render={(name, record) => (
            <Button
              type="link"
              href={`/dashboard/product-database/products/${record.productMatrixId}/edit`}
            >
              {name}
            </Button>
          )}
        />
        <Column
          title="NI"
          key="ni"
          dataIndex="ni"
          sortType="string"
          render={(value) => renderCopyableValue(value)}
        />
        <Column
          title="SI"
          key="si"
          dataIndex="si"
          sortType="string"
          render={(value) => renderCopyableValue(value)}
        />
      </CustomTable>
    </>
  );
};

export default FoodstuffsSkuSapTable;
